import styled from 'styled-components';
import * as React from 'react';

export const SubscriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacer-M);
  max-width: 1400px;
  margin: 0 auto;
  background: #f6f8fb;
  border-radius: 18px;

  .step-section {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .step-label {
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #6b7280;
    padding-bottom: 10px;
  }

  @media (max-width: 768px) {
    padding: var(--spacer-M) var(--spacer-S);
    gap: var(--spacer-M);
    border-radius: var(--spacer-M);
  }
`;


export const PlanHeaderGrid = styled.div<{ $planCount: number, children?: React.ReactNode; className?: string }>`
  display: grid;
  grid-template-columns: minmax(140px, 22%) repeat(${({ $planCount }) => Math.max($planCount, 1)}, minmax(180px, 1fr));
  gap: 14px;
  align-items: stretch;
  padding: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 16px;
  min-width: 760px;

  .plan-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 22px;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    transition: all 0.2s ease, transform 0.2s ease;
    cursor: pointer;
    text-align: left;
    position: relative;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }

    &.selected {
      border: 2px solid #0066cc;
      background: linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%);
      box-shadow: 0 8px 24px rgba(0, 102, 204, 0.15);
    }

    &.current:not(.selected) {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }

    &.current.selected {
      border: 2px solid #0066cc;
      background: linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%);
      box-shadow: 0 8px 24px rgba(0, 102, 204, 0.15);
    }
  }

  .plan-card__title {
    font-weight: 800;
    font-size: 16px;
    color: #0f172a;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .plan-card__subtitle {
    font-size: 13px;
    color: #6b7280;
    line-height: 1.4;
    min-height: 36px;
    font-weight: 400;
  }

  .plan-card__price {
    font-size: 20px;
    font-weight: 800;
    color: #0066cc;
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-top: 8px;
  }

  .plan-card__per {
    font-size: 14px;
    font-weight: 600;
    color: #4b5563;
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(140px, 28%) repeat(${({ $planCount }) => Math.max($planCount, 1)}, minmax(160px, 1fr));
    padding: var(--spacer-S);
    padding-bottom: 0;
    min-width: 640px;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    border-bottom: none;
    padding: var(--spacer-M);
    gap: var(--spacer-M);
    min-width: unset;

    button.plan-card {
      width: 100%;
    }
  }
`;

export const PlanHeaderSpacer = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

export const PlanTableWrapper = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;

  .table-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);

    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 999px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }

  @media (max-width: 768px) {
    border-radius: 14px;
  }
`;

export const PlanTable = styled.table<{ $planCount: number, children?: React.ReactNode; className?: string }>`
  width: 100%;
  min-width: 760px;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 0;
  table-layout: fixed;
  font-size: 14px;
  color: #0f172a;

  col.feature-col {
    width: 22%;
  }

  col.plan-col {
    width: calc(78% / ${({ $planCount }) => Math.max($planCount, 1)});
  }

  th, td {
    padding: 8px 12px;
    border-bottom: 1px solid #f3f4f6;
    border-right: 1px solid #f3f4f6;
    text-align: center;
    background: #fff;
    line-height: 1.3;
    font-size: 13px;
  }

  th {
    background-color: transparent;
    color: #0f172a;
    font-weight: 600;
    border-color: #f3f4f6;
  }

  td:first-child,
  th:first-child {
    text-align: left;
    font-weight: 400;
    color: #6b7280;
    border-right: none;
  }

  th.selected,
  td.selected {
    background-color: #f0f7ff;
    font-weight: 600;
    color: #0f172a;
  }

  th:first-child,
  td:first-child {
    position: sticky;
    left: 0;
    z-index: 2;
    background: #fafbfc;
    box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.06);
  }

  th:first-child {
    z-index: 3;
  }

  td.selected:first-child {
    z-index: 3;
    background: #fafbfc;
  }

  tr:last-child td {
    border-bottom: none;
  }

  td:last-child,
  th:last-child {
    border-right: none;
  }

  @media (max-width: 900px) {
    min-width: 640px;

    th, td {
      padding: var(--spacer-S);
    }

    col.feature-col {
      width: 28%;
    }

    col.plan-col {
      width: calc(72% / ${({ $planCount }) => Math.max($planCount, 1)});
    }
  }

  @media (max-width: 768px) {
    min-width: unset;
    display: block;

    thead {
      display: none;
    }

    tbody, tr, td {
      display: block;
      width: 100%;
    }

    colgroup {
      display: none;
    }

    tr {
      margin-bottom: var(--spacer-M);
      border: 1px solid #e5eaf3;
      border-radius: var(--spacer-S);
      overflow: hidden;
    }

    td {
      padding: var(--spacer-S) var(--spacer-M);
      border: none;
      border-bottom: 1px solid #e5eaf3;
      display: flex;
      justify-content: space-between;
      align-items: center;
      text-align: left;

      &:first-child {
        position: static;
        background: #f9fbff;
        font-weight: 700;
        font-size: 0.95rem;
        box-shadow: none;
      }

      &:last-child {
        border-bottom: none;
      }

      &.selected {
        box-shadow: none;
      }

      &::before {
        content: attr(data-label);
        font-weight: 600;
        color: #6b7280;
        text-align: left;
      }
    }

    td:first-child::before {
      content: '';
    }
  }
`;

export const SlidersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 24px;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    padding: var(--spacer-M);
  }
`;

export const SliderContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;

  .slider-row {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 28px;
  }

  .slider-row .MuiSlider-root {
    flex: 1;
  }

  @media (max-width: 768px) {
    .slider-row {
      grid-template-columns: 1fr;
      gap: var(--spacer-S);
    }
  }
`;

export const SliderLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

export const SliderValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  min-width: 120px;
  text-align: right;
  line-height: 1.4;

  .included-text {
    font-size: 12px;
    color: #10b981;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    text-align: left;
    min-width: unset;
  }
`;

export const AddonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 18px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const AddonCard = styled.div`
  padding: 22px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
    border-color: #d1d5db;
  }
`;

export const AddonTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
`;

export const AddonDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`;

export const PriceCalculatorBox = styled.div`
  position: sticky;
  bottom: var(--spacer-M);
  background-color: white;
  border: 2px solid #0066cc;
  border-radius: 12px;
  padding: 18px 20px 12px 20px;
  box-shadow: 0 4px 16px rgba(0, 102, 204, 0.12);
  z-index: 100;
  display: grid;
  grid-template-columns: 1fr 260px;
  gap: 24px;
  align-items: start;

  .price-breakdown {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .price-total-section {
    text-align: right;
    padding: 16px 0 16px 20px;
    border-left: 1px solid #e5e7eb;
    min-width: 240px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .price-main {
      font-size: 26px;
      font-weight: 800;
      color: #0066cc;
      line-height: 1.1;
      transition: all 0.2s ease;

      .per-month {
        font-size: 12px;
        font-weight: 600;
        color: #6b7280;
      }
    }

    .price-secondary {
      font-size: 12px;
      font-weight: 400;
      color: #6b7280;
      margin-bottom: 6px;
    }
  }

  @media (max-width: 768px) {
    position: relative;
    bottom: unset;
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 20px;

    .price-breakdown {
      gap: 6px;
    }

    .price-total-section {
      border-left: none;
      border-top: 1px solid #e5e7eb;
      padding-left: 0;
      padding-top: 20px;
      min-width: unset;
      text-align: center;

      .price-main {
        font-size: 28px;
      }
    }
  }
`;

export const PriceRow = styled.div<{ children?: React.ReactNode; className?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 12px;
  color: #374151;
  line-height: 1.3;

  span:first-child {
    font-weight: 400;
  }

  span:last-child {
    font-weight: 600;
  }
`;

export const ModuleName = styled.span<{ id?: string, children?: React.ReactNode; className?: string }>`
  display: flex;
  align-items: center;
  gap: var(--spacer-XS);
  cursor: help;
  color: #6b7280;
  font-weight: 500;
`;

export const AvailabilityPill = styled.span<{ children?: React.ReactNode; className?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  padding: 4px 8px;
  border-radius: 999px;
  font-weight: 600;
  letter-spacing: 0.01em;
  font-size: 12px;

  &.available {
    background: #d1fae5;
    color: #065f46;
  }

  &.unavailable {
    background: transparent;
    color: #9ca3af;
    font-weight: 400;
  }
`;

export const CurrentPlanPill = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 1;
`;

export const NewPlanPill = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #eff6ff;
  color: #0066cc;
  border: 1px solid #93c5fd;
  border-radius: 12px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 1;
`;

export const PaymentInfoIcon = styled.span<{ id?: string, children?: React.ReactNode; className?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #147142;
  color: #e7f5ed;
  font-weight: 800;
  font-size: 0.65rem;
  cursor: help;
  font-style: normal;
`;

export const PlanMetaNote = styled.div`
  font-size: 12px;
  color: #9ca3af;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-weight: 400;
`;

export const PriceComparisonRow = styled(PriceRow)<{ children?: React.ReactNode; className?: string }>`
  font-size: 11px;
  color: #6b7280;
  padding: 3px 0;

  &.increase {
    color: #dc2626;

    span:last-child {
      color: #dc2626;
    }
  }

  &.decrease {
    color: #10b981;

    span:last-child {
      color: #10b981;
    }
  }
`;

export const PriceComparisonSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const PriceBreakdownColumn = styled.div<{ children?: React.ReactNode; className?: string }>`
  display: flex;
  flex-direction: column;
  gap: 5px;

  &.current {
    opacity: 0.7;
    background: #f9fafb;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  &.changes {
    background: #eff6ff;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #bfdbfe;
  }
`;

export const SectionHeader = styled.div<{ children?: React.ReactNode; className?: string }>`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;

  &.changes-header {
    color: #0066cc;
  }
`;

export const ChangeIndicator = styled.span<{ children?: React.ReactNode; className?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  margin-right: 4px;

  &.added {
    background: #d1fae5;
    color: #065f46;
  }

  &.removed {
    background: #fee2e2;
    color: #991b1b;
  }

  &.modified {
    background: #fef3c7;
    color: #92400e;
  }

  &.unchanged {
    background: #e5e7eb;
    color: #6b7280;
  }
`;

export const OverviewCard = styled.div`
  background: #ffffff;
  border: 2px solid #0066cc;
  border-radius: 16px;
  padding: 28px 32px;
  box-shadow: 0 4px 12px rgba(0, 102, 204, 0.12);
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    padding: 20px;
    gap: 18px;
  }
`;

export const OverviewHeader = styled.div`
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: 0.01em;
  }

  @media (max-width: 768px) {
    h2 {
      font-size: 18px;
    }
  }
`;

export const OverviewDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

export const OverviewDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  .label {
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .value {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;

    &.plan-name {
      font-size: 18px;
      font-weight: 700;
      color: #0066cc;
    }

    &.price {
      font-size: 18px;
      font-weight: 700;
      color: #10b981;
    }
  }

  @media (max-width: 768px) {
    .value {
      font-size: 15px;

      &.plan-name,
      &.price {
        font-size: 17px;
      }
    }
  }
`;

export const OverviewActions = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 8px;
  flex-wrap: wrap;

  button {
    flex: 1;
    min-width: 140px;
    font-weight: 600;
    text-transform: none;
    padding: 10px 20px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;

    button {
      width: 100%;
    }

    span {
      width: 100%;
    }
  }
`;

export const OverviewUsageSummary = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
`;
